import { darken } from 'polished';
import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import { Matrix } from '../../../platform/geom/Matrix';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { RenderBounds, RenderBox, RenderInner, usePose, usePressable } from '../../canvas/visualization/RenderHelpers';
import { RenderText } from '../../canvas/visualization/RenderText';
import { SmartButton, } from './SmartButton';
import { SmartButtonLabelTextSettings } from './SmartButtonLabelField';

const RenderPress = React.memo<{ area: Rect, interaction: string, children: ReactNode }>(({ area, interaction, children }) => {

    const transform =
        (interaction === 'idle' ? Matrix.translate(0, 0).toCSS() : null) ??
        (interaction === 'hover' ? Matrix.translate(0, -3).toCSS() : null) ??
        (interaction === 'active' ? Matrix.translate(0, 1).toCSS() : null);

    const style = {
        ...area.toCSS(),
        transform,
        transition: 'transform 75ms ease-out',
    };

    return <RenderBox {...style}>
        {children}
    </RenderBox>;
});

const RenderShadow = React.memo<{ area: Rect, color: string }>(({ area, color }) => {
    return <RenderBox {...area.toCSS()} background={darken(.1, color)} borderRadius="8px" />;
});

const RenderButton = React.memo<{ area: Rect, color: string }>(({ area, color }) => {
    return <RenderBox {...area.toCSS()} background={color} borderRadius="8px" />;
});

const RenderLabel = ({ object }: { object: SmartButton }) => {
    const customize = useCallback((props: any) => ({ ...props, color: Colors.contrastColorFor(object.background) }), [object.background]);
    if (object.layout.label.area() === 0) return null;
    return <RenderText
        bounds={object.layout.label}
        settings={SmartButtonLabelTextSettings}
        value={object.label}
        customize={customize} />;
};

export const SmartButtonVisual: FC<{ object: SmartButton, children?: ReactNode }> = ({ object }) => {

    const { base, inner } = object.layout;
    const shadow = useMemo(() => inner.offset([0, 2]), [inner]);
    const interaction = usePressable(object, 'default');
    const { beingEdited } = usePose(object);


    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderShadow area={shadow} color={object.background} />
                <RenderPress area={base} interaction={interaction}>
                    <RenderButton area={inner} color={object.background} />
                    {!beingEdited && <RenderLabel object={object} />}
                </RenderPress>
            </RenderInner>
        </RenderBounds >
    </>;
};
